import { PermissionEnum } from '@creditsights/api-ui-web';
import { Button, ButtonMode, ButtonSize, Checkbox, Dropdown, Icon, ICONS, Loader, useGlobalModalContext } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { partition } from 'lodash';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Accordion } from 'semantic-ui-react';
import { MODAL_TYPES } from '../../../components/modal/GlobalModal';
import { USER_EMAIL_PREFERENCES_CACHE_KEY } from '../../../Constants';
import { emailSettings } from '../../../data/email-preferences';
import useFetchUserEmailPreferences from '../../../hooks/user/useFetchUserEmailPreferences';
import useFetchUserPermissions from '../../../hooks/user/useFetchUserPermissions';
import useSaveUserEmailPreferences from '../../../hooks/user/useSaveUserEmailPreferences';
import { OnboardingStepProps } from '../Onboarding';
import styles from './EmailPreferences.module.scss';

const cx = classNames.bind(styles);

function EmailPreferences({ onContinue, onBack, show, isSaving }: OnboardingStepProps): ReactElement | null {
  const queryClient = useQueryClient();
  const { data: preferences, isLoading } = useFetchUserEmailPreferences({ enabled: show });
  const { mutateAsync: saveEmailPreferences } = useSaveUserEmailPreferences();
  const { data: userPermissions, isLoading: isPermissionsLoading } = useFetchUserPermissions();
  const { showModal } = useGlobalModalContext();

  const researchAccess = useMemo(() => userPermissions?.permissions.includes(PermissionEnum.RESEARCH), [userPermissions]);

  const [tags, setSelectedTags] = useState<string[]>([]);
  const [collapsedAccordions, setCollapsedAccordions] = useState<string[]>([]);
  const [alertTimeZone, setAlertTimeZone] = useState<any>();
  const [pauseAllNotifications, setPauseAllNotifications] = useState(false);
  const [editorsPicks, setEditorsPicks] = useState(false);

  useEffect(() => {
    setSelectedTags(preferences?.tags ?? []);
    setEditorsPicks(preferences?.editorsPicks ?? false);
    setPauseAllNotifications(preferences?.pauseAllNotifications ?? false);
    setAlertTimeZone(preferences?.alertTimeZone);
  }, [preferences]);

  const save = () => {
    return saveEmailPreferences(
      {
        alertTimeZone,
        editorsPicks,
        pauseAllNotifications,
        tags,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([USER_EMAIL_PREFERENCES_CACHE_KEY]);
        },
      }
    );
  };

  const isTagSelected = (tagId) => tags.indexOf(tagId) !== -1;
  const isAccordionOpen = (name) => collapsedAccordions.indexOf(name) === -1;

  const handleTagToggle = (tagId) => {
    const shouldUnselect = isTagSelected(tagId);
    const newValue = shouldUnselect ? tags.filter((t) => t !== tagId) : [...tags, tagId];
    setSelectedTags(newValue);
  };

  const handleAllTagsChanged = (event, tagIds) => {
    event.stopPropagation();

    const [selected, notSelected] = partition(tagIds, isTagSelected);
    const shouldUnselect = selected.length > 0;
    const newValue = shouldUnselect ? tags.filter((t) => !selected.includes(t)) : [...tags, ...notSelected];

    setSelectedTags(newValue);
  };

  const handleAccordionToggle = (name) => {
    const newValue = isAccordionOpen(name) ? [...collapsedAccordions, name] : collapsedAccordions.filter((t) => t !== name);
    setCollapsedAccordions(newValue);
  };

  const handleTimeZoneSelected = (newValue) => {
    setAlertTimeZone(newValue);
  };

  const handleSkip = () => {
    showModal(MODAL_TYPES.CONFIRM_SKIP_ONBOARDING);
  };

  const renderSources = ({ research = false, news = false, covenants = false }) => {
    return (
      <>
        {research && <div className={cx('source', 'research')}>Research</div>}
        {news && <div className={cx('source', 'news')}>News</div>}
        {covenants && <div className={cx('source', 'covenants')}>Covenants</div>}
      </>
    );
  };

  const renderTagPills = (items) => {
    return (
      <div className={cx('pills-ctx')}>
        {items.map((item) => {
          const isSelected = isTagSelected(item.tagId);

          return (
            <button type="button" className={cx('pill', { active: isSelected, inactive: !isSelected })} onClick={() => handleTagToggle(item.tagId)} key={item.tagId}>
              <i className={cx('check icon')} />
              <div className={cx('text')}>{item.name}</div>
            </button>
          );
        })}
      </div>
    );
  };

  const renderTagCheckboxes = (items) => {
    return items.map((item) => {
      const isSelected = isTagSelected(item.tagId);

      return (
        <div className={cx('sub-section')} key={item.tagId}>
          <Checkbox className={cx('checkbox')} label={item.name} checked={isSelected} onChange={() => handleTagToggle(item.tagId)} dataTestId={item.tagId} />
          {item.description && <div className={cx('description')}>{item.description}</div>}
        </div>
      );
    });
  };

  const renderTimezoneDropdown = (timezones) => {
    return (
      <div className={cx('dropdown-ctx')}>
        <div className={cx('label')}>Receive email at 6:00am</div>
        <Dropdown value={alertTimeZone} placeholder="Select timezone" options={timezones} onOptionSelect={(e) => handleTimeZoneSelected(e)} />
      </div>
    );
  };

  const renderAccordion = (sectionConfig, mode: 'pills' | 'checkboxes') => {
    const { title, options, description, sources, timezones } = sectionConfig;
    const tagIds = options.map((o) => o.tagId);
    const sectionChecked = tagIds?.some(isTagSelected);
    const isOpen = isAccordionOpen(title);

    return (
      <>
        <Accordion.Title className={cx('title')} onClick={() => handleAccordionToggle(title)}>
          <div className={cx('icon')}>
            <Icon name={isOpen ? ICONS.CaretDown : ICONS.CaretRight} height={14} width={14} />
          </div>
          {title}
          {renderSources(sources)}
          <Checkbox className={cx('checkbox')} toggle checked={sectionChecked} dataTestId={title} onChange={(e) => handleAllTagsChanged(e, tagIds)} />
        </Accordion.Title>
        <Accordion.Content className={cx('content')} active={isOpen}>
          {description && <div className="description">{description}</div>}
          {timezones && renderTimezoneDropdown(timezones)}
          {mode === 'checkboxes' && renderTagCheckboxes(options)}
          {mode === 'pills' && renderTagPills(options)}
        </Accordion.Content>
      </>
    );
  };

  if (!show) {
    return null;
  }
  if (!isLoading && !isPermissionsLoading) {
    return (
      <div className={cx('body')}>
        <div className={cx('contents')}>
          <Accordion className={cx('accordion')}>
            {renderAccordion(emailSettings.digests, 'checkboxes')}
            {researchAccess && renderAccordion(emailSettings.morningComment, 'pills')}
            {researchAccess && renderAccordion(emailSettings.weeklyReader, 'pills')}
            {researchAccess && renderAccordion(emailSettings.summaries, 'checkboxes')}
          </Accordion>
        </div>

        <div className={cx('footer')}>
          <div className={cx('contents')}>
            <Button mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} loading={isSaving} onClick={onBack}>
              Previous
            </Button>
            <div className={cx('spacer')} />
            <Button className={cx('skip-button')} mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} disabled={isSaving} onClick={handleSkip}>
              Skip Onboarding
            </Button>
            <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} loading={isSaving} onClick={() => onContinue(save)}>
              Complete
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return <Loader className="cs-loader" position="center" />;
}

export default EmailPreferences;
